import {
  Button,
  Empty,
  Input,
  Popconfirm,
  Skeleton,
  Space,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  RedoOutlined,
  HistoryOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { errorMsg, successMsg } from "../../Utils/Utils";
import {
  toGetNplBrands,
  toGetNplHistory,
  toUpdateNplHistoryOwner,
  toDeleteNplHistory,
} from "./Api";

const History = ({ onReopen }) => {
  const [brands, setBrands] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [query, setQuery] = useState("");

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const rows = await toGetNplHistory();
      setHistory(rows);
    } catch (e) {
      errorMsg(e.response?.data?.errors || "Impossible de charger l'historique");
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    toGetNplBrands().then(setBrands);
    load();
  }, []);

  const brandName = (code) =>
    Object.entries(brands).find(([, value]) => value === code)?.[0] || code;

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditingValue(row.owner || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const saveEdit = async (row) => {
    const owner = editingValue.trim() || "inconnu";
    try {
      await toUpdateNplHistoryOwner(row.id, owner);
      setHistory(
        history.map((h) => (h.id === row.id ? { ...h, owner } : h)),
      );
      successMsg("Propriétaire mis à jour");
    } catch (e) {
      errorMsg(
        e.response?.data?.errors || "Impossible de mettre à jour le propriétaire",
      );
    }
    cancelEdit();
  };

  const remove = async (id) => {
    try {
      await toDeleteNplHistory(id);
      setHistory(history.filter((h) => h.id !== id));
      successMsg("Recherche supprimée de l'historique");
    } catch (e) {
      errorMsg(e.response?.data?.errors || "Impossible de supprimer");
    }
  };

  const reopen = (row) => {
    if (onReopen) {
      onReopen({ vin: row.vin, brand: row.brand, lang: row.lang || "fr" });
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? history.filter(
        (h) =>
          (h.vin || "").toLowerCase().includes(q) ||
          (h.owner || "inconnu").toLowerCase().includes(q),
      )
    : history;

  return (
    <div>
      <div
        className="npl-card npl-card-pad"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <HistoryOutlined style={{ fontSize: 18, color: "#059669" }} />
        <span className="npl-section-label" style={{ marginBottom: 0 }}>
          Historique des recherches VIN
        </span>
        <Input
          className="npl-vin-input npl-history-search"
          style={{ flex: 1, minWidth: 220, maxWidth: 380, marginLeft: "auto" }}
          prefix={<SearchOutlined className="npl-search-icon" />}
          placeholder="Rechercher par VIN ou propriétaire…"
          allowClear
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="npl-badge-pill">{history.length} recherche(s)</span>
        <Button
          icon={<RedoOutlined />}
          className="npl-btn-secondary"
          onClick={() => load(false)}
        >
          Actualiser
        </Button>
      </div>

      <Skeleton active loading={loading} />

      {!loading && history.length === 0 && (
        <div className="npl-card">
          <div className="npl-empty-box">
            <Empty description="Aucune recherche enregistrée pour le moment" />
          </div>
        </div>
      )}

      {!loading && history.length > 0 && (
        <div className="npl-card" style={{ padding: 0, overflow: "hidden" }}>
          {filtered.length === 0 ? (
            <div className="npl-empty-box">
              <Empty description="Aucune correspondance pour cette recherche" />
            </div>
          ) : (
            <div className="npl-table-wrap">
              <table className="npl-table">
                <thead>
                  <tr>
                    <th style={{ width: 170 }}>Date</th>
                    <th style={{ width: 180 }}>VIN</th>
                    <th>Marque</th>
                    <th style={{ width: 220 }}>Propriétaire</th>
                    <th style={{ width: 190, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                  <tr
                    key={row.id}
                    style={{
                      cursor: "default",
                      opacity: editingId === row.id ? 0.65 : 1,
                    }}
                  >
                    <td>
                      <span className="npl-cell-sub">
                        {new Date(row.createdAt).toLocaleString("fr-FR")}
                      </span>
                    </td>
                    <td>
                      <span className="npl-cell-mono">{row.vin}</span>
                    </td>
                    <td>
                      <span className="npl-cell-main">{brandName(row.brand)}</span>
                    </td>
                    <td>
                      {editingId === row.id ? (
                        <Space size={4}>
                          <Input
                            size="small"
                            style={{ width: 130 }}
                            value={editingValue}
                            autoFocus
                            onChange={(e) => setEditingValue(e.target.value)}
                            onPressEnter={() => saveEdit(row)}
                          />
                          <Button
                            size="small"
                            type="primary"
                            className="npl-btn-primary"
                            icon={<CheckOutlined />}
                            onClick={() => saveEdit(row)}
                          />
                          <Button
                            size="small"
                            className="npl-btn-secondary"
                            icon={<CloseOutlined />}
                            onClick={cancelEdit}
                          />
                        </Space>
                      ) : (
                        <Space size={6}>
                          <span
                            className="npl-cell-main"
                            style={{ fontWeight: 500 }}
                          >
                            {row.owner || "inconnu"}
                          </span>
                          <span
                            className="npl-micro-btn"
                            role="button"
                            onClick={() => startEdit(row)}
                          >
                            <EditOutlined /> Modifier
                          </span>
                        </Space>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Space size={6}>
                        <span
                          className="npl-micro-btn"
                          role="button"
                          onClick={() => reopen(row)}
                        >
                          <RedoOutlined /> Rouvrir
                        </span>
                        <Popconfirm
                          title="Supprimer cette recherche ?"
                          okText="Supprimer"
                          cancelText="Annuler"
                          onConfirm={() => remove(row.id)}
                        >
                          <span className="npl-micro-btn" role="button">
                            <DeleteOutlined /> Supprimer
                          </span>
                        </Popconfirm>
                      </Space>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {!loading && history.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Le propriétaire est "inconnu" par défaut, modifiez-le pour associer
            une recherche à une personne.
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

export default History;